import {useCallback, useMemo} from 'react';
import {skipToken} from '@apollo/client';

import {PageId} from 'modules/decisionsNext/types/pages';

import {
  PageSettingsDocument,
  PageSettingsQuery,
  usePageSettingsSuspenseQuery,
} from 'api/pageSettings/pageSettings.generated';
import {useSavePageSettingsMutation} from 'api/pageSettings/savePageSettings.generated';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {decisionsGraphQLClient} from 'modules/decisionsNext/api';

const pageId = PageId.UserAgreement;

export const useUserAgreement = () => {
  const withUserAgreementGate = useFeatureFlag(FeatureFlags.UserAgreementGate);

  const {data} = usePageSettingsSuspenseQuery(
    withUserAgreementGate
      ? {
          client: decisionsGraphQLClient,
          variables: {
            pageId,
          },
        }
      : skipToken
  );

  return useMemo(
    () => ({
      hasAcceptedUserAgreement: withUserAgreementGate
        ? data?.userPageSettings?.customSettings?.find(setting => setting.id === 'hasAcceptedUserAgreement')?.value ===
            'true' || false
        : true,
    }),
    [data, withUserAgreementGate]
  );
};

export const useAcceptUserAgreement = () => {
  const {hasAcceptedUserAgreement} = useUserAgreement();

  const [savePageSettings, {loading}] = useSavePageSettingsMutation({});

  const acceptUserAgreement = useCallback(() => {
    if (hasAcceptedUserAgreement) {
      return;
    }

    savePageSettings({
      variables: {
        settings: {
          pageId,
          customSettings: [{id: 'hasAcceptedUserAgreement', value: 'true'}],
        },
      },
      optimisticResponse: {
        saveUserPageSettings: {
          pageId,
          sections: null,
          filter: null,
          customSettings: [{id: 'hasAcceptedUserAgreement', value: 'true'}],
        },
      },
      update(cache, {data}) {
        if (data?.saveUserPageSettings) {
          cache.writeQuery<PageSettingsQuery>({
            query: PageSettingsDocument,
            variables: {pageId},
            data: {
              userPageSettings: {
                pageId,
                filter: null,
                sections: null,
                customSettings: data.saveUserPageSettings.customSettings,
              },
            },
          });
        }
      },
    });
  }, [hasAcceptedUserAgreement, savePageSettings]);

  return useMemo(
    () => ({
      loading,
      acceptUserAgreement,
    }),
    [loading, acceptUserAgreement]
  );
};
